import { useCallback, useEffect, useRef, useState } from 'react';

import { useRefState } from '@/hooks/use-ref-state';

import type { IResponse } from '@/utils/fetch';

export interface IFetchResult<TItem> {
    cursor?: string;
    list?: TItem[];
    hasMore?: boolean;
}

interface IUseInfiniteListStatusOptions<TResponseData, IRequestQuery, TItem> {
    fetcher: (params: IRequestQuery) => Promise<IResponse<TResponseData>>;
    initialCursor?: string;
    initialHasMore?: boolean;
    queryParams?: Record<string, unknown>;
    transformResponseData?: (data: TResponseData) => IFetchResult<TItem>;
}

export interface IUseInfiniteListStatusResult<TItem> {
    isError: boolean;
    isLoading: boolean;
    isEmpty: boolean;
    hasMore: boolean;
    list: TItem[];
    loadMore: () => Promise<void>;
    refresh: () => Promise<void>;
}

export function useInfiniteListStatus<TItem, TResponseData, IRequestQuery>({
    fetcher,
    initialCursor = '',
    initialHasMore = true,
    queryParams = {},
    transformResponseData,
}: IUseInfiniteListStatusOptions<TResponseData, IRequestQuery, TItem>): IUseInfiniteListStatusResult<TItem> {
    const [isError, setIsError] = useState(false);
    const [isLoading, isLoadingRef, setIsLoading] = useRefState(true);
    const [isEmpty, setIsEmpty] = useState(false);
    const [hasMore, setHasMore] = useState(initialHasMore);
    const cursorRef = useRef(initialCursor);
    const [list, setList] = useState<TItem[]>([]);

    const fetchList = useCallback(
        async (options: { cursor: string; append: boolean }) => {
            const { cursor, append } = options;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const { data } = await fetcher({
                cursor,
                ...queryParams,
            } as IRequestQuery);
            if (!data) {
                throw new Error('No data returned');
            }
            const transformResult = transformResponseData ? transformResponseData(data) : data;
            const {
                cursor: newCursor = '',
                list: newList = [],
                hasMore: nextHasMore = false,
            } = transformResult as IFetchResult<TItem>;

            setIsError(false);

            if (newList?.length === 0) {
                if (append && isLoadingRef.current) {
                    setIsEmpty(true);
                    return;
                }
                if (!append) {
                    setIsEmpty(true);
                    setList([]);
                    cursorRef.current = newCursor;
                    setHasMore(nextHasMore);
                    return;
                }
            }

            setIsEmpty(false);
            setList(prev => (append ? [...prev, ...((newList ?? []) as TItem[])] : ((newList ?? []) as TItem[])));
            cursorRef.current = newCursor;
            setHasMore(nextHasMore);
        },
        [fetcher, isLoadingRef, queryParams, transformResponseData]
    );

    const loadMore = async () => {
        try {
            await fetchList({ cursor: cursorRef.current, append: true });
        } catch {
            if (isLoadingRef.current) {
                setIsError(true);
            }
            throw new Error('No data returned');
        } finally {
            setIsLoading(false);
        }
    };

    const refresh = useCallback(async () => {
        cursorRef.current = initialCursor;
        try {
            await fetchList({ cursor: initialCursor, append: false });
        } catch {
            throw new Error('No data returned');
        }
    }, [fetchList, initialCursor]);

    useEffect(() => {
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isError,
        isLoading,
        isEmpty,
        hasMore,
        list,
        loadMore,
        refresh,
    };
}
